import React from 'react'
import PropTypes from 'prop-types'
import shallowEqual from 'recompose/shallowEqual'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'

import clsx from 'clsx'

import Select from '../Select'
import { MenuItem } from '../Menu'
import TextField from '../TextField'
import { InputLabel } from 'pmt-ui/Input'
import { FormControl } from 'pmt-ui/Form'

import { withStyles } from 'pmt-ui/styles'

import Flag from 'pmt-utils/country/flag'
import {
  getCountryCodes,
  getCountryFromCountryCode,
  getCountryDialCodeFromCountryCode,
  formatPhoneNumberAsYouType,
  parse,
} from 'pmt-utils/phone'
import { getLocale, getCountryCodeForLocale } from 'pmt-modules/i18n/utils'

const styles = theme => ({
  root: {
    flexDirection: 'row',
    height: 48,
  },
  phoneNumberRoot: {
    marginTop: 16,
    flexGrow: 1,
  },
  label: {
    top: 0,
    left: 0,
    position: 'absolute',
    transform: 'translate(0, 1.5px) scale(0.75)', // TODO: use theme?
    transformOrigin: 'top left',
  },
  flagIcon: {
    width: 19,
    height: 15,
    marginRight: theme.spacing(1),
    borderRadius: 2,
  },
  selectedValue: {
    position: 'absolute',
    top: `calc(50% - 9px)`,
    display: 'flex',
    height: '100%',
    width: '100%',
    // TODO: remove after migrating to TypoGraphy
    fontSize: 16,
    paddingTop: 2,
  },
  // New CSS from massive material update
  selectRoot: {
    position: 'relative',
    width: 10,
  },
})

const getInitialState = () => {
  const locale = getLocale()

  if (locale) {
    let countryCode = getCountryCodeForLocale(locale)

    if (countryCode) {
      const countryDialCode = getCountryDialCodeFromCountryCode(countryCode)

      if (countryDialCode) {
        //We return countryCode+countryDialCode only if we recognized both at the same time
        return {
          localeDetected: true,
          countryCode: countryCode,
          countryDialCode: countryDialCode,
          phoneNumber: countryDialCode,
          formattedPhoneNumber: countryDialCode,
          nationalNumber: '',
          touched: false,
        }
      }
    }
  }

  //We return France/+33 as default
  return {
    localeDetected: false,
    countryCode: 'FR',
    countryDialCode: '+33',
    phoneNumber: '+33',
    formattedPhoneNumber: '+33',
    nationalNumber: '',
    touched: false,
  }
}

const makeSureStartWithPlusOrEmpty = inputValue => {
  return inputValue.startsWith('+') || inputValue === '' ? inputValue : `+${inputValue}`
}

class PhoneField extends React.Component {
  static propTypes = {
    /**
     * The value of the text field.
     */
    value: PropTypes.any,
  }

  escapeSetFormattedNumber = false // allow us to skip infinite loop but setting right phone number and skip error display while there's none

  state = getInitialState()

  componentDidMount() {
    //We apply the initCountryCode if set and either override boolean is true or locale wasn't detected
    if (
      this.props.initCountryCode &&
      (this.props.initCountryCodeOverridesBrowserLocale || !this.state.localeDetected)
    ) {
      this.handleCountryCodeChange(null, this.props.initCountryCode)
    }
  }

  componentWillReceiveProps(nextProps) {
    //console.log('[componentWillReceiveProps] nextProps: ', nextProps)
    //console.log('[componentWillReceiveProps] state: ', this.state)

    const nextPropsValue = nextProps.value

    const libPhoneNumber = parse(nextPropsValue)
    if (libPhoneNumber && libPhoneNumber.isValid()) {
      const newState = {
        ...this.state,
        phoneNumber: libPhoneNumber.number,
        nationalNumber: libPhoneNumber.nationalNumber,
        formattedPhoneNumber: libPhoneNumber.formatInternational(),
      }

      if (libPhoneNumber.country) {
        const dialCode = getCountryDialCodeFromCountryCode(libPhoneNumber.country)

        if (dialCode) {
          newState.countryCode = libPhoneNumber.country
          newState.countryDialCode = makeSureStartWithPlusOrEmpty(dialCode)
        }
      }

      this.setState(newState, () => {
        // on certain devices (old samsung), cursor "jumps" on previous char
        // we position it at the end on setState callback
        // setTimeout is necessary to ensure it's working
        if (!isNil(this.input)) {
          const cursorPosition = this.input.selectionStart
          setTimeout(
            () => this.input && this.input.setSelectionRange(cursorPosition, cursorPosition),
            0
          )
        }
      })
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !shallowEqual(this.props, nextProps) || !shallowEqual(this.state, nextState)
  }

  handleChange = (asYouTypeResults, newState) => {
    const country = asYouTypeResults.country
    const number = asYouTypeResults.number

    if (country) {
      const dialCode = getCountryDialCodeFromCountryCode(country)

      if (dialCode) {
        newState.countryCode = country
        newState.countryDialCode = dialCode
      }
    }

    if (number) {
      newState.phoneNumber = number.number
      newState.nationalNumber = number.nationalNumber

      if (number.country) {
        const dialCode = getCountryDialCodeFromCountryCode(number.country)

        if (dialCode) {
          newState.countryCode = number.country
          newState.countryDialCode = makeSureStartWithPlusOrEmpty(dialCode)
        }
      }
    }

    this.setState(newState, () => {
      this.props.onChange(`${this.state.phoneNumber}`)
    })
  }

  handleCountryCodeChange = (event, countryCode) => {
    if (countryCode === this.state.countryCode) return

    const countryDialCode = getCountryDialCodeFromCountryCode(countryCode)

    const extractedNationalNumber = this.state.phoneNumber.startsWith(this.state.countryDialCode)
      ? this.state.phoneNumber.slice(this.state.countryDialCode.length)
      : this.state.nationalNumber

    const newFormattedNumber = makeSureStartWithPlusOrEmpty(
      `${countryDialCode}${extractedNationalNumber}`
    )
    const asYouTypeResults = formatPhoneNumberAsYouType(newFormattedNumber, this.state.countryCode)

    const inputFormatted = asYouTypeResults.input
    const nationalNumber = asYouTypeResults.nationalNumber

    const newState = {
      ...this.state,
      countryCode: countryCode,
      countryDialCode: countryDialCode,
      phoneNumber: inputFormatted.replace(/[ -()]/g, ''),
      formattedPhoneNumber: inputFormatted,
      nationalNumber: nationalNumber,
    }

    this.handleChange(asYouTypeResults, newState)
  }

  handleInputChange = event => {
    this.escapeSetFormattedNumber = false

    const inputValue = makeSureStartWithPlusOrEmpty(event.target.value)
    const asYouTypeResults = formatPhoneNumberAsYouType(inputValue, this.state.countryCode)

    const inputFormatted = asYouTypeResults.input
    const nationalNumber = asYouTypeResults.nationalNumber

    const newState = {
      ...this.state,
      phoneNumber: inputFormatted.replace(/[ -()]/g, ''),
      formattedPhoneNumber: inputFormatted,
      nationalNumber: nationalNumber,
      touched: true,
    }

    this.handleChange(asYouTypeResults, newState)
  }

  render() {
    const { classes, className, label, required, disabled } = this.props

    try {
      const selectElement = (
        <Select
          name="selectCountryCode"
          // current value
          value={this.state.countryCode}
          // display of the current value
          renderValue={() => {
            const country = getCountryFromCountryCode(this.state.countryCode)

            return (
              <div className={classes.selectedValue}>
                <Flag
                  className={classes.flagIcon}
                  country={this.state.countryCode}
                  alt={country.name}
                />
              </div>
            )
          }}
          autoWidth={true}
          onChange={event => this.handleCountryCodeChange(event, event.target.value)}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: '100%',
              },
            },
          }}
          classes={{
            root: classes.selectRoot,
          }}
          disabled={disabled}
        >
          {getCountryCodes().map(({ name, dial_code, code }) => (
            <MenuItem key={code} value={code}>
              <Flag className={classes.flagIcon} country={code} alt={name} />
              {name}
              &nbsp;&nbsp;
              {dial_code}
            </MenuItem>
          ))}
        </Select>
      )

      const displayAsError = this.state.touched && this.props.error

      return (
        <div>
          <FormControl className={clsx(className, classes.root)}>
            <InputLabel htmlFor="phone" className={classes.label} error={displayAsError}>
              {label}
              {required ? ' *' : ''}
            </InputLabel>

            {selectElement}

            <TextField
              id="phone"
              type="tel"
              classes={{
                root: classes.phoneNumberRoot,
              }}
              inputRef={elem => (this.input = elem)}
              onChange={event => {
                this.handleInputChange(event)
              }}
              value={this.state.formattedPhoneNumber}
              error={displayAsError}
              placeholder={this.props.placeholder}
              disabled={disabled}
              required={required}
            />
          </FormControl>
          {displayAsError &&
            !isEmpty(this.props.helperText) && (
              <p className="colorError u-fontSize12 u-marginTop8">{this.props.helperText}</p>
            )}
        </div>
      )
    } catch (e) {
      console.error(e)
    }
  }
}

export default withStyles(styles)(PhoneField)
